import { ArrowRight, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { RH_CARDS, SITUATIONS, scrollTo } from './consulting-data';

export const ConsultingTab2 = () => (
  <div data-testid="panel-tab2">
    {/* Intro RH */}
    <section className="bg-white py-20" data-testid="rh-intro">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[5fr_7fr] gap-16 items-start">
        <div>
          <div className="inline-flex items-center gap-2 text-[#F59E0B] text-xs font-semibold tracking-[0.22em] uppercase mb-4">
            <span className="w-7 h-[1.5px] bg-[#F59E0B]" /> Le constat
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937] mb-4">Les erreurs RH les plus coûteuses sont celles qu'on ne voit pas</h2>
          <div className="w-11 h-0.5 bg-[#F59E0B] mb-6" />
          <div className="bg-[#0D9488]/10 rounded-lg p-7 border-l-[3px] border-[#0D9488]">
            <p className="text-sm text-[#0D9488] leading-relaxed">En Polynésie française, le droit du travail a son propre code <strong className="font-semibold text-[#0F766E]">(CPFP)</strong>, ses propres conventions collectives territoriales, ses propres délais de procédure et sa propre jurisprudence prud'homale. Ce que vous savez du droit du travail métropolitain <strong className="font-semibold text-[#0F766E]">ne s'applique pas nécessairement ici.</strong></p>
          </div>
          <div className="mt-6">
            <Button onClick={() => scrollTo('contact-consulting')} className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-7 py-5">
              Demander un audit gratuit <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
        <div>
          <p className="text-lg italic text-[#1F2937] leading-relaxed border-l-[3px] border-[#F59E0B] pl-6 mb-6">"Un contrat non conforme, une procédure mal menée, un salarié dont le statut est flou - ce sont des risques qui s'accumulent en silence pendant des années, jusqu'au jour ou ils coutent tres cher."</p>
          <p className="text-sm text-gray-500 leading-relaxed mb-4">La plupart des dirigeants de TPE polynésiennes gèrent leurs ressources humaines eux-memes, sans formation RH, en s'appuyant sur leur bon sens. C'est souvent suffisant dans un premier temps. Mais a mesure que l'entreprise grandit - les approximations deviennent des risques.</p>
          <p className="text-sm text-gray-500 leading-relaxed">Chez AERH Consulting, nous intervenons précisément à ce moment : quand le dirigeant sent qu'il a besoin d'un regard extérieur competent, ancre dans la réalité polynésienne.</p>
        </div>
      </div>
    </section>

    {/* 4 cartes RH */}
    <section className="bg-[#F8FAFC] py-20" data-testid="rh-cards">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="inline-flex items-center gap-2 text-[#F59E0B] text-xs font-semibold tracking-[0.22em] uppercase mb-4">
          <span className="w-7 h-[1.5px] bg-[#F59E0B]" /> Nos prestations
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937] max-w-[520px] mb-12">Quatre familles d'intervention, une même logique : sécuriser votre entreprise</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {RH_CARDS.map(card => (
            <div key={card.num} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`rh-card-${card.num}`}>
              <div className="p-7 border-b border-gray-100 flex gap-4 items-start">
                <div className="w-12 h-12 flex-shrink-0 bg-[#0D9488]/10 rounded-lg flex items-center justify-center">
                  <card.icon className="w-6 h-6 text-[#0D9488]" />
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#F59E0B] mb-1">{card.num} — {card.tag}</p>
                  <h3 className="text-lg font-bold text-[#1F2937] leading-tight">{card.title}</h3>
                </div>
              </div>
              <div className="p-7">
                <p className="text-sm text-gray-500 leading-relaxed mb-5">{card.desc}</p>
                <ul className="space-y-0">
                  {card.items.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-gray-500 py-2 border-b border-gray-100 last:border-0">
                      <ArrowRight className="w-3.5 h-3.5 text-[#0D9488] flex-shrink-0 mt-0.5" /> {item}
                    </li>
                  ))}
                </ul>
                {card.badge && (
                  <div className="mt-4 bg-[#F59E0B]/10 border border-[#F59E0B]/25 rounded-lg p-3 text-xs text-[#1F2937] leading-relaxed">
                    <strong className="text-[#F59E0B]">Notre engagement :</strong> {card.badge.split(': ')[1]}
                  </div>
                )}
                {card.note && <p className="mt-4 text-xs text-gray-400 italic leading-relaxed">{card.note}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Situations concretes */}
    <section className="bg-white py-20" data-testid="situations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-end mb-12">
          <div>
            <div className="inline-flex items-center gap-2 text-[#F59E0B] text-xs font-semibold tracking-[0.22em] uppercase mb-4">
              <span className="w-7 h-[1.5px] bg-[#F59E0B]" /> Situations réelles
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937]">Des situations que vous avez peut-etre déjà vecues</h2>
          </div>
          <p className="text-sm text-gray-500 leading-relaxed">Chaque dirigeant de TPE polynésienne en a vécu au moins une. Ce sont ces moments où l'on réalise qu'un conseil RH compétent aurait évité un problème coûteux.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {SITUATIONS.map(s => (
            <div key={s.num} className="bg-white rounded-xl border border-gray-200 p-8 relative overflow-hidden hover:border-gray-300 hover:shadow-md transition-all" data-testid={`situation-${s.num}`}>
              <span className="absolute top-5 right-6 text-5xl font-bold text-gray-100 select-none">{s.num}</span>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#0D9488] mb-3">{s.sector}</p>
              <h4 className="text-base font-bold text-[#1F2937] mb-3 max-w-[280px] leading-snug">{s.title}</h4>
              <p className="text-sm text-gray-500 leading-relaxed mb-4">{s.desc}</p>
              <div className="text-xs font-medium text-amber-800 bg-amber-50 border-l-2 border-amber-500 py-2 px-3 rounded-r-lg leading-relaxed">
                <AlertTriangle className="w-3 h-3 inline mr-1" />{s.risk}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Externalisation RH */}
    <section className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] py-16 sm:py-20 relative overflow-hidden" data-testid="ext-section">
      <span className="absolute right-[-10px] bottom-[-60px] text-[20rem] font-bold text-white/[0.025] leading-none select-none pointer-events-none">RH</span>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16">
        <div>
          <div className="inline-flex items-center gap-2 text-[#F59E0B]/85 text-xs font-semibold tracking-[0.22em] uppercase mb-4">
            <span className="w-7 h-[1.5px] bg-[#F59E0B]/85" /> Externalisation RH
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-5">
            Un DRH a temps partage.<br /><span className="italic text-[#F59E0B]">Sans le cout d'un recrutement.</span>
          </h2>
          <p className="text-sm text-white/60 leading-relaxed mb-4">Beaucoup de dirigeants ont besoin d'un appui RH regulier, mais pas a plein temps. L'externalisation partielle de la fonction RH permet de bénéficier d'une expertise de haut niveau, à un cout maîtrise.</p>
          <p className="text-sm text-white/60 leading-relaxed mb-6">Nous devenons votre interlocuteur RH de référence - pour les questions du quotidien comme pour les decisions sensibles.</p>
          <p className="text-xs text-white/40 italic leading-relaxed border-t border-white/10 pt-4 mb-7">Ce service est distinct de Moana Paye, qui prend en charge la paie et les obligations sociales déclaratives.</p>
          <Button onClick={() => scrollTo('contact-consulting')} className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-7 py-5">
            Nous contacter <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
        <div className="space-y-3.5">
          {[
            { t: 'Présence régulière planifiée', d: "Intervention mensuelle ou bimensuelle selon votre volume RH - en présentiel à Papeete ou en distanciel." },
            { t: 'Réponse rapide sur les situations sensibles', d: "Un problème RH surgit aujourd'hui - vous avez une réponse argumentée et sécurisée rapidement." },
            { t: 'Veille règlementaire PF incluse', d: "Évolutions du CPFP, nouvelles conventions, modifications des seuils sociaux - vous êtes informé avant que ça vous impacte." },
            { t: 'Interface avec vos conseils', d: "Coordination avec votre comptable, votre avocat ou votre gestionnaire de paie - pour que tout le monde parle le même langage." }
          ].map((f, i) => (
            <div key={i} className="bg-white/[0.05] border border-white/[0.08] rounded-lg p-5 flex gap-4 items-start hover:border-[#F59E0B]/30 transition-colors">
              <Clock className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-white mb-1">{f.t}</p>
                <p className="text-xs text-white/50 leading-relaxed">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Témoignages T2 */}
    <section className="bg-[#F8FAFC] py-20" data-testid="temo-tab2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="inline-flex items-center gap-2 text-[#F59E0B] text-xs font-semibold tracking-[0.22em] uppercase mb-4">
          <span className="w-7 h-[1.5px] bg-[#F59E0B]" /> Ce qu'ils disent
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937] mb-10">Retours d'experience</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { text: "Un cabinet qui connaît la réalité de nos îles, qui parle vrai et qui sait écouter. Pour les questions RH sensibles, je n'aurais pas su naviguer seul dans le CPFP.", author: 'Dirigeante', role: 'Secteur touristique - Bora Bora' },
            { text: "AERH a su nous accompagner au-delà du recrutement : conseils RH, repositionnement de poste, médiation. Un vrai partenaire de terrain, pas juste un prestataire.", author: 'Dirigeant', role: 'TPE du BTP - Moorea' },
            { text: "Ce que j'apprécie : la disponibilité, l'efficacité, la transparence. Des professionnels du RH, surtout des gens de confiance qui comprennent notre contexte polynésien.", author: 'Responsable administratif', role: 'Secteur construction - Papeete' }
          ].map((t, i) => (
            <div key={i} className="bg-white rounded-xl p-8 border border-gray-200 border-t-[3px] border-t-[#0D9488] hover:-translate-y-1 hover:shadow-lg transition-all">
              <p className="text-3xl text-[#0D9488]/20 font-bold leading-none mb-3">"</p>
              <p className="text-sm text-gray-600 italic leading-relaxed mb-6">{t.text}</p>
              <div className="w-8 h-[1.5px] bg-[#F59E0B] mb-3" />
              <p className="text-xs font-semibold text-[#1F2937] uppercase tracking-wider">{t.author}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{t.role}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);
